// useUnsavedChangesGuard — feature-side hook that registers a single
// editable surface with the shared `UnsavedChangesProvider` and exposes a
// guarded close handler plus the effective dismiss-disabled flag.
//
// @aiPurpose Wrap close affordances of an editable Modal/Drawer/DetailSheet
//   so they consult the generic provider before dropping local form state.
// @aiSecurity Inputs are primitive `dirty`/`busy` booleans and a
//   confirmed-close callback. Never accept form objects, password values,
//   or anything sensitive — keep the generic infrastructure value-agnostic.
import { useCallback, useEffect, useRef } from 'react';
import { useUnsavedChangesContext } from './UnsavedChangesProvider.js';

export interface UseUnsavedChangesGuardOpts {
  /** True when the surface has meaningful unsaved changes vs its baseline. */
  dirty: boolean;
  /** True while a save/create/reset/delete/upload mutation owned by the surface is in flight. */
  busy?: boolean;
  /** Original close path (e.g. parent's `onClose`). Called when the user confirms discard or when surface is clean. */
  onConfirmClose: () => void;
}

export interface UseUnsavedChangesGuardResult {
  /** Drop-in replacement for any close affordance. Idempotent while the prompt is open. */
  requestClose: () => void;
  /**
   * Guarded helper for non-close destructive actions that would lose
   * dirty local state (e.g. tab/view switches that unmount the editor).
   */
  confirmDestructive: (action: () => void) => void;
  /**
   * One-shot bypass for the next blocker dispatch. Call right before a
   * synchronous `navigate(...)` (or other route change) in an `onSuccess`
   * handler that has already cleared the underlying dirty state via
   * `reset()`. Without this, the blocker callback runs before React has
   * committed the cleared state and would intercept the navigation.
   */
  bypassNext: () => void;
  /**
   * Pass-through value matching `busy || warningOpen`. Feed into
   * `Modal.dismissDisabled` / `Drawer.dismissDisabled` / `DetailSheet.dismissDisabled`
   * so Escape, backdrop, and back-button paths stay aligned with the prompt.
   */
  effectiveDismissDisabled: boolean;
  /** True while the generic prompt is open. */
  warningOpen: boolean;
}

export function useUnsavedChangesGuard(
  opts: UseUnsavedChangesGuardOpts,
): UseUnsavedChangesGuardResult {
  const { dirty, busy = false, onConfirmClose } = opts;
  const ctx = useUnsavedChangesContext();

  // Refs mirror the current props synchronously during render so the
  // provider's blocker callback (which can fire mid-render via React Router
  // navigation) reads the live value. `useEffect`-based ref updates lag by
  // one commit, which races a `reset() → navigate(...)` chain inside an
  // `onSuccess` handler: the form is already clean, but the ref still
  // reports dirty, so the navigation gets blocked and never reaches the
  // target URL. Render-time assignment is the canonical fix for "ref that
  // just shadows a prop".
  const dirtyRef = useRef(dirty);
  const busyRef = useRef(busy);
  const onConfirmCloseRef = useRef(onConfirmClose);
  dirtyRef.current = dirty;
  busyRef.current = busy;
  onConfirmCloseRef.current = onConfirmClose;

  // Register exactly once per mount; the entry reads live refs so the
  // provider sees current dirty/busy values without re-registering on
  // every render.
  useEffect(() => {
    const unregister = ctx.register({
      isDirty: () => dirtyRef.current,
      isBusy: () => busyRef.current,
    });
    return unregister;
  }, [ctx]);

  const { requestPrompt, warningOpen, bypassNext } = ctx;

  const requestClose = useCallback(() => {
    if (warningOpen) return;
    if (busyRef.current) return;
    if (!dirtyRef.current) {
      onConfirmCloseRef.current();
      return;
    }
    requestPrompt({ onDiscard: () => onConfirmCloseRef.current() });
  }, [warningOpen, requestPrompt]);

  const confirmDestructive = useCallback(
    (action: () => void) => {
      if (warningOpen) return;
      if (busyRef.current) return;
      if (!dirtyRef.current) {
        action();
        return;
      }
      requestPrompt({ onDiscard: action });
    },
    [warningOpen, requestPrompt],
  );

  return {
    requestClose,
    confirmDestructive,
    bypassNext,
    effectiveDismissDisabled: busy || warningOpen,
    warningOpen,
  };
}
